import type { GamePresenceResponse, RoomsResponse } from '../../entities/room/types';
import type { HallBetsResponse } from '../../shared/hall/betFeedView';
import { apiFetch, authHeader, jsonBody } from '../../shared/api/httpClient';
import { clientHeaders } from '../../shared/lib/device';

export function fetchRooms() {
  return apiFetch<RoomsResponse>('/api/rooms');
}

export function fetchGamePresence() {
  return apiFetch<GamePresenceResponse>('/api/rooms/presence', {
    cache: 'no-store',
  });
}

/**
 * 全站最近下注，跨玩法。契约 `doc/HALL_BET_FEED_CONTRACT.md`。
 *
 * 鉴权可选：未登录也返回内容，且游客也看到真名与头像（§1，2026-09-14 起）。
 * 端点未上线时抛 404，调用方据此降级回 Milhar-only（见 `shouldFallBackToMilhar`）。
 */
export function fetchHallBets(token: string | null | undefined, limit = 20) {
  return apiFetch<HallBetsResponse>(`/api/v1/hall/bets?limit=${encodeURIComponent(String(limit))}`, {
    cache: 'no-store',
    headers: {
      ...clientHeaders(),
      // Optional. The feed reads the same for guests and members now, but the
      // token still scopes the rows to the account's country (a guest is scoped
      // by the country header instead).
      ...authHeader(token),
    },
  });
}

export type HallPresenceResponse = {
  ok: boolean;
  /** Distinct visitors online site-wide, same number as `/api/rooms/presence` `total`. */
  online: number;
  presence_ttl_sec: number;
};

/**
 * Site-wide presence heartbeat: "this client has the app open right now".
 *
 * Every open client sends it (see `useHallPresence`), not only the room page —
 * the room heartbeat only counts people standing inside a room, which left the
 * lobby with no honest number for "N playing right now". The server unions the
 * two by visitor key, so a player inside a room is counted once.
 *
 * `visitorId` is the same local guest id the room heartbeat uses; the server
 * ignores it for a signed-in viewer and needs it to de-duplicate a guest.
 */
export function heartbeatHall(token: string | null | undefined, visitorId: string) {
  return apiFetch<HallPresenceResponse>('/api/v1/hall/presence', {
    method: 'POST',
    headers: {
      ...clientHeaders(),
      ...authHeader(token),
    },
    body: jsonBody({ visitor_id: visitorId }),
  });
}
